import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { AutoComplete, Input, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { StatisticOperator } from '../../../../../../enums/assetStatistic';
import { useStatisticFieldValues } from '../../../../../../hooks/useAssetStatistics';
import { getAssetManagerErrorMessage } from '../../../../../../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../../../../../../services/AssetManager/assetManager.constants';
import Notification from '../../../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../../../enums/notificationTypes';
import type { AssetLayerField } from '../../../../../../types/InfrastructureManagerTypes';
import {
  BOOLEAN_VALUES,
  CONNECTOR_OPTIONS,
  FIELD_VALUES_SEARCH_DEBOUNCE_MS,
  OPERATOR_LABELS,
  VALUELESS_OPERATORS,
  type ConditionRow,
  type WhereConditionField,
} from '../constants';
import styles from './whereConditionRow.module.scss';

interface WhereConditionRowProps {
  index: number;
  row: ConditionRow;
  layerId: number | null;
  fields: AssetLayerField[];
  isLoadingFields: boolean;
  dropdownClassName: string;
  onChangeField: (index: number, field: WhereConditionField, value: string | null) => void;
  onRemove: (index: number) => void;
}

// The backend takes a date as YYYY-MM-DD; a listed value may carry a time.
const toDateValue = (value: unknown) => String(value).replace(/[T\s].*$/, '');

const CONNECTOR_SELECT_OPTIONS = CONNECTOR_OPTIONS.map((option) => ({ value: option, label: option }));

const WhereConditionRow = ({ index, row, layerId, fields, isLoadingFields, dropdownClassName, onChangeField, onRemove }: WhereConditionRowProps) => {
  const [isValuesOpen, setIsValuesOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fieldDef = fields.find(({ name }) => name === row.field);
  const isBoolean = fieldDef?.type === 'BOOLEAN';
  const isDate = fieldDef?.type === 'DATE';
  const isValueless = !!row.operator && VALUELESS_OPERATORS.has(row.operator);
  // Contains is free text; Is / Is Not match exactly and a date has one accepted format, so those pick from existing values.
  const isContains = row.operator === StatisticOperator.CONTAINS;
  const picksValue = row.operator === StatisticOperator.IS || row.operator === StatisticOperator.IS_NOT || isDate;
  const suggestsValues = !!row.operator && !isValueless && !isContains;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), FIELD_VALUES_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { values, isLoadingValues, valuesError } = useStatisticFieldValues(
    { layerId: layerId ?? 0, field: row.field ?? '', search: debouncedSearch },
    isValuesOpen && suggestsValues && !isBoolean && !!row.field && layerId !== null,
  );

  useEffect(() => {
    if (!valuesError) return;
    Notification({
      message: assetManagerServiceMessages.STATISTICS.FIELD_VALUES_ERROR,
      description: getAssetManagerErrorMessage(valuesError),
      type: NotificationTypes.ERROR,
    });
  }, [valuesError]);

  const fieldOptions = useMemo(() => fields.map(({ name }) => ({ value: name, label: name })), [fields]);
  const operatorOptions = useMemo(
    () => (fieldDef?.allowedOperators ?? []).map((operator) => ({ value: operator, label: OPERATOR_LABELS[operator] })),
    [fieldDef],
  );
  const valueOptions = useMemo(() => {
    const listed = (values ?? []).map((value) => (isDate ? toDateValue(value) : String(value)));
    return (isBoolean ? BOOLEAN_VALUES : [...new Set(listed)]).map((value) => ({ value }));
  }, [isBoolean, isDate, values]);

  // A cleared picker reports undefined; the row stores it as empty.
  const handleFieldChange = (field: WhereConditionField) => (nextValue: string | undefined) => onChangeField(index, field, nextValue ?? null);

  const handleValueChange = (nextValue: string | undefined) => {
    setSearch(nextValue ?? '');
    onChangeField(index, 'value', nextValue || null);
  };

  const handleRemove = () => onRemove(index);

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => onChangeField(index, 'value', event.target.value || null);

  // The typed term only narrows the list; the value is whatever was picked.
  const handleOptionChange = (nextValue: string | undefined) => {
    setSearch('');
    onChangeField(index, 'value', nextValue ?? null);
  };

  const handleValuesOpenChange = (open: boolean) => {
    setIsValuesOpen(open);
    if (!open) setSearch('');
  };

  const renderValueInput = () => {
    if (isValueless) return null;
    if (isContains) return <Input className={styles.textInput} placeholder="Value" value={row.value ?? ''} onChange={handleTextChange} />;
    if (picksValue) {
      return (
        <Select
          className={styles.select}
          popupClassName={dropdownClassName}
          popupMatchSelectWidth={false}
          placeholder="Value"
          allowClear
          showSearch
          filterOption={false}
          value={row.value ?? undefined}
          options={valueOptions}
          loading={isLoadingValues}
          notFoundContent={isLoadingValues ? 'Loading…' : undefined}
          onSearch={setSearch}
          onChange={handleOptionChange}
          onDropdownVisibleChange={handleValuesOpenChange}
        />
      );
    }
    return (
      <AutoComplete
        className={styles.select}
        popupClassName={dropdownClassName}
        popupMatchSelectWidth={false}
        placeholder="Value"
        allowClear
        value={row.value ?? undefined}
        options={valueOptions}
        // No loading prop on AutoComplete; null keeps it closed when empty so free text still works.
        notFoundContent={isLoadingValues ? 'Loading…' : null}
        onChange={handleValueChange}
        onDropdownVisibleChange={setIsValuesOpen}
        disabled={!row.operator}
      />
    );
  };

  return (
    <div className={styles.row}>
      {index > 0 && (
        <Select
          className={styles.connectorSelect}
          popupClassName={dropdownClassName}
          popupMatchSelectWidth={false}
          value={row.connector ?? undefined}
          options={CONNECTOR_SELECT_OPTIONS}
          onChange={handleFieldChange('connector')}
        />
      )}
      <Select
        className={styles.select}
        popupClassName={dropdownClassName}
        popupMatchSelectWidth={false}
        placeholder="Category"
        allowClear
        showSearch
        value={row.field ?? undefined}
        options={fieldOptions}
        loading={isLoadingFields}
        notFoundContent={isLoadingFields ? 'Loading…' : undefined}
        onChange={handleFieldChange('field')}
        disabled={layerId === null}
      />
      <Select
        className={styles.select}
        popupClassName={dropdownClassName}
        popupMatchSelectWidth={false}
        placeholder="Condition"
        allowClear
        value={row.operator ?? undefined}
        options={operatorOptions}
        onChange={handleFieldChange('operator')}
        disabled={!row.field}
      />
      {renderValueInput()}
      {index > 0 && (
        <button type="button" className={styles.removeButton} onClick={handleRemove} aria-label="Remove condition">
          <CloseOutlined />
        </button>
      )}
    </div>
  );
};

export default WhereConditionRow;
